"use client";

import { useEffect, useRef, useState } from "react";
import EquationToolbar from "./EquationToolbar";
import { configureMathliveSpeech } from "./mathliveSpeech";

// Defer importing Mathlive to the client to avoid SSR issues
// We only reference window-dependent APIs inside effects.

type EquationEditorProps = {
  value?: string;
  onChange?: (latex: string) => void;
  placeholder?: string;
  className?: string;
};

type MathfieldHandle = HTMLElement & {
  setValue: (latex: string) => void;
  getValue: (format?: string) => string;
  focus?: () => void;
  placeholder?: string;
  virtualKeyboardMode?: "auto" | "manual" | "onfocus" | "off";
  mathVirtualKeyboardPolicy?: "auto" | "onfocus" | "manual" | "off";
  smartFence?: boolean;
  smartSuperscript?: boolean;
  removeExtraneousParentheses?: boolean;
  executeCommand?: (selector: string | (string | unknown)[]) => boolean;
  selectedText?: (format?: string) => string;
};

type VirtualKeyboard = {
  show: () => void;
  container?: HTMLElement | null;
  boundingRect?: { height: number } | null;
  addEventListener?: (
    type: string,
    cb: (ev?: Event | CustomEvent<unknown>) => void
  ) => void;
  removeEventListener?: (
    type: string,
    cb: (ev?: Event | CustomEvent<unknown>) => void
  ) => void;
  visible?: boolean;
};

export default function EquationEditor({
  value = "",
  onChange,
  placeholder = "Type LaTeX or use the keyboard…",
  className,
}: EquationEditorProps) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const keyboardRef = useRef<HTMLDivElement | null>(null);
  const fieldRef = useRef<MathfieldHandle | null>(null);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const speechUtteranceRef = useRef<SpeechSynthesisUtterance | null>(null);

  useEffect(() => {
    let disposed = false;
    const cleanupFns: Array<() => void> = [];
    async function setup() {
      if (typeof window === "undefined") return;
      // Configure global MathLive assets and speech hooks (idempotent)
      await configureMathliveSpeech({
        onStart: () => setIsSpeaking(true),
        onEnd: () => setIsSpeaking(false),
        onError: () => setIsSpeaking(false),
      });
      await import("mathlive");

      // Asset directories & hooks are already configured above

      // Create and mount the custom element
      const mf = document.createElement("math-field") as MathfieldHandle;
      mf.style.display = "block";
      mf.style.width = "100%";
      mf.className =
        "rounded-md border border-border bg-background text-foreground min-h-24 px-3 py-3 text-lg";
      mf.placeholder = placeholder;
      // Virtual keyboard sensible defaults for desktops & touch
      mf.virtualKeyboardMode = "manual"; // toggle via button we add below
      mf.mathVirtualKeyboardPolicy = "manual"; // keep open across focus changes
      mf.smartFence = true;
      mf.smartSuperscript = true;
      mf.removeExtraneousParentheses = true;

      // Initial value is set in a separate effect to avoid remount path dependency

      mf.addEventListener("input", () => {
        const latex = mf.getValue("latex");
        onChange?.(latex);
      });

      const host = hostRef.current;
      if (!host || disposed) return;
      host.appendChild(mf as unknown as Node);
      fieldRef.current = mf;
      // Autofocus when tool opens
      try {
        mf.focus?.();
      } catch {}

      // Initialize shared virtual keyboard container with high z-index
      const kbd = (
        window as unknown as { mathVirtualKeyboard?: VirtualKeyboard }
      ).mathVirtualKeyboard;
      if (kbd) {
        // Render keyboard inside this tool only
        const container = keyboardRef.current;
        if (container) {
          kbd.container = container;
          container.style.setProperty("--keyboard-zindex", "1");
          const initialHeight =
            (window as unknown as { mathVirtualKeyboard?: VirtualKeyboard })
              .mathVirtualKeyboard?.boundingRect?.height ?? 0;
          container.style.height = `${initialHeight}px`;
          container.style.display = "block";
          // Let MathLive control spacing/keycap sizing via its own theme

          // Sync MathLive theme (light/dark) with our app theme
          const applyKeyboardTheme = () => {
            const themeAttr =
              document.documentElement.getAttribute("data-theme") === "dark"
                ? "dark"
                : "light";
            container.setAttribute("theme", themeAttr);
          };
          applyKeyboardTheme();
          const themeObserver = new MutationObserver(applyKeyboardTheme);
          themeObserver.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ["data-theme"],
          });
          cleanupFns.push(() => themeObserver.disconnect());

          const onGeometry = () => {
            try {
              const height =
                (window as unknown as { mathVirtualKeyboard?: VirtualKeyboard })
                  .mathVirtualKeyboard?.boundingRect?.height ?? 0;
              container.style.height = `${height}px`;
            } catch {}
          };
          // Prevent the keyboard from hiding (drag/resize can blur the field)
          const onBeforeToggle = (
            ev?: Event | CustomEvent<{ visible: boolean }>
          ) => {
            const detail = (ev as CustomEvent<{ visible: boolean }> | undefined)
              ?.detail;
            if (kbd.visible === true && detail?.visible === false) {
              if (
                ev &&
                "preventDefault" in ev &&
                typeof ev.preventDefault === "function"
              ) {
                ev.preventDefault();
              }
              // Keep our panel visible
              container.style.display = "block";
            }
          };

          (
            window as unknown as { mathVirtualKeyboard?: VirtualKeyboard }
          ).mathVirtualKeyboard?.addEventListener?.(
            "geometrychange",
            onGeometry
          );
          (
            window as unknown as { mathVirtualKeyboard?: VirtualKeyboard }
          ).mathVirtualKeyboard?.addEventListener?.(
            "before-virtual-keyboard-toggle",
            onBeforeToggle
          );

          // Apply height immediately on mount
          try {
            onGeometry();
          } catch {}

          cleanupFns.push(() => {
            try {
              (
                window as unknown as { mathVirtualKeyboard?: VirtualKeyboard }
              ).mathVirtualKeyboard?.removeEventListener?.(
                "geometrychange",
                onGeometry
              );
            } catch {}
            try {
              (
                window as unknown as { mathVirtualKeyboard?: VirtualKeyboard }
              ).mathVirtualKeyboard?.removeEventListener?.(
                "before-virtual-keyboard-toggle",
                onBeforeToggle
              );
            } catch {}
          });

          // Also ensure we hide and detach the keyboard when this component unmounts
          cleanupFns.push(() => {
            try {
              const vk = (
                window as unknown as { mathVirtualKeyboard?: VirtualKeyboard }
              ).mathVirtualKeyboard as unknown as
                | (VirtualKeyboard & { hide?: () => void })
                | undefined;
              if (
                vk &&
                (vk as unknown as { container?: HTMLElement }).container ===
                  container
              ) {
                try {
                  (vk as unknown as { visible?: boolean }).visible = false;
                } catch {}
                vk.hide?.();
                try {
                  (
                    vk as unknown as { container?: HTMLElement | null }
                  ).container = null;
                } catch {}
              }
            } catch {}
            try {
              container.style.display = "none";
              container.style.height = "0px";
            } catch {}
          });

          // Ensure it's shown immediately
          try {
            kbd.visible = true;
          } catch {}
          kbd.show?.();
          try {
            onGeometry();
          } catch {}
          try {
            requestAnimationFrame(() => {
              try {
                onGeometry();
              } catch {}
            });
          } catch {}
        }
      }
    }
    setup();
    return () => {
      disposed = true;
      const mf = fieldRef.current as unknown as HTMLElement | null;
      if (mf && (mf as HTMLElement).parentElement)
        (mf as HTMLElement).parentElement!.removeChild(mf as HTMLElement);
      fieldRef.current = null;
      for (const fn of cleanupFns) fn();
    };
  }, [placeholder, onChange, value]);

  // Sync incoming value without remounting the field
  useEffect(() => {
    try {
      if (value != null && fieldRef.current) {
        const current = fieldRef.current.getValue?.("latex");
        if (current !== value) fieldRef.current.setValue?.(value);
      }
    } catch {}
  }, [value]);

  // Keyboard is shown automatically; no button needed

  const copyLatex = async () => {
    const latex = fieldRef.current?.getValue?.("latex");
    if (latex) {
      try {
        await navigator.clipboard.writeText(latex);
      } catch {}
    }
  };

  const clearField = () => {
    try {
      fieldRef.current?.setValue?.("");
      onChange?.("");
    } catch {}
  };

  const stopSpeaking = () => {
    try {
      if (typeof window !== "undefined" && "speechSynthesis" in window) {
        window.speechSynthesis.cancel();
      }
    } catch {}
    speechUtteranceRef.current = null;
    setIsSpeaking(false);
  };

  const speakEquation = () => {
    const field = fieldRef.current;
    if (!field) return;
    try {
      // Ensure focus so commands operate on the mathfield
      field.focus?.();
      // Cancel any ongoing speech
      stopSpeaking();
      // Try built-in speak of the whole expression
      const okString = field.executeCommand?.('speak("all")');
      const okArray = okString || field.executeCommand?.(["speak", "all"]);
      if (okArray) return;
      // Emulate the keyboard workflow: select all, then speak selection
      const selOk =
        field.executeCommand?.("selectAll") ||
        field.executeCommand?.(["selectAll"]);
      const speakSelOk =
        selOk &&
        (field.executeCommand?.('speak("selection")') ||
          field.executeCommand?.(["speak", "selection"]));
      if (speakSelOk) return;
      // Fallback: get speakable text and delegate to speakHook
      let text = field.getValue?.("spoken");
      if (!text) text = field.getValue?.("spoken-text");
      if (!text) text = field.getValue?.("latex");
      if (text) {
        try {
          import("mathlive").then((mod) => {
            const mfe = (
              mod as unknown as {
                MathfieldElement?: { speakHook?: (t: string) => void };
              }
            ).MathfieldElement;
            mfe?.speakHook?.(text as string);
          });
        } catch {}
      }
    } catch {}
  };

  useEffect(() => {
    return () => {
      // Stop any ongoing speech on unmount
      try {
        if (typeof window !== "undefined" && "speechSynthesis" in window) {
          window.speechSynthesis.cancel();
        }
      } catch {}
    };
  }, []);

  return (
    <div
      className={`${className ?? ""} h-full flex flex-col`}
      aria-label="Equation editor"
    >
      <EquationToolbar
        onCopy={copyLatex}
        onClear={clearField}
        onSpeak={speakEquation}
        onStop={stopSpeaking}
        speaking={isSpeaking}
      />
      <div ref={hostRef} className="no-drag flex-1 min-h-0" />
      <div
        ref={keyboardRef}
        className="mt-2 rounded-t-md border border-border bg-card/70 text-foreground no-drag"
        style={{
          position: "sticky",
          bottom: 0,
          width: "100%",
          minHeight: 0,
          overflow: "hidden",
        }}
        aria-label="Virtual keyboard container"
      />
    </div>
  );
}
